import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { dashboardAPI } from '../../services/api'

const SLOTS = ['morning', 'midday', 'evening']

// VH-247 stage 2: today on the cycle home — intake against the target, this week's sessions, weight,
// supplements. Read from the Dashboard's summary, which stays until stage 5 removes the Dashboard.
export function TodayCard() {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboardSummary'],
    queryFn: () => dashboardAPI.getSummary().then((response) => response.data),
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) return <div className="card"><div className="card-body text-sm text-gray-500">{t('common.loading')}</div></div>
  if (isError || !data) {
    return (
      <div className="card" data-testid="today-card">
        <div className="card-body text-sm">
          <p className="text-red-600">{t('home.today.error')}</p>
          <button type="button" className="btn-outline mt-2 text-xs" onClick={() => refetch()}>{t('home.today.retry')}</button>
        </div>
      </div>
    )
  }

  const sc = data.stat_cards || {}
  const points = data.body_weight_trend?.data_points || []
  const weight = points.length ? points[points.length - 1].weight_kg : null
  const change = data.body_weight_trend?.weight_change_30d
  const schedule = data.supplement_schedule || {}
  const doses = SLOTS.flatMap((slot) => (schedule[slot] || []).map((item) => ({ ...item, slot })))
  const goal = sc.today_calories_goal

  return (
    <section className="card" data-testid="today-card">
      <div className="card-body space-y-3 text-sm">
        <h2 className="text-lg font-semibold text-gray-900">{t('home.today.title')}</h2>
        <ul className="divide-y divide-gray-100">
          <li className="py-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-gray-600">{t('home.today.calories')}</span>
            <Link to="/eat" className="font-medium text-gray-900 hover:text-primary-700" data-testid="today-calories">
              {goal
                ? t('home.today.caloriesOfGoal', { value: Math.round(sc.today_calories || 0), goal: Math.round(goal) })
                : t('home.today.caloriesNoGoal', { value: Math.round(sc.today_calories || 0) })}
            </Link>
          </li>
          <li className="py-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-gray-600">{t('home.today.sessions')}</span>
            <Link to="/programs" className="font-medium text-gray-900 hover:text-primary-700">
              {t('home.today.sessionsOfTotal', {
                done: sc.weekly_workout_sessions_completed || 0, total: sc.weekly_workout_sessions_total || 0,
              })}
            </Link>
          </li>
          <li className="py-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-gray-600">{t('home.today.weight')}</span>
            <span className="font-medium text-gray-900" data-testid="today-weight">
              {weight == null
                ? t('home.today.noWeight')
                : change == null
                  ? t('home.today.weightValue', { value: weight })
                  : t('home.today.weightChange', { value: weight, change: `${change > 0 ? '+' : ''}${change}` })}
            </span>
          </li>
          <li className="py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-gray-600">{t('home.today.supplements')}</span>
              <Link to="/supplements" className="font-medium text-gray-900 hover:text-primary-700">
                {t('home.today.doses', { count: doses.length })}
              </Link>
            </div>
            {doses.length > 0 && (
              <ul className="mt-1 text-xs text-gray-500 space-y-0.5">
                {doses.map((d) => (
                  <li key={`${d.id}-${d.slot}`} className="flex flex-wrap justify-between gap-2">
                    <span className="break-words min-w-0">{d.name}</span>
                    <span>{t(`dashboard.${d.slot}`)}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>
      </div>
    </section>
  )
}

export default TodayCard
